import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  createElement,
} from 'react';
import { StyleSheet, type ViewStyle } from 'react-native';
import { isNotEmpty } from '../../utils';

interface WebViewProps {
  title?: string;
  source: {
    uri?: string;
    html?: string;
    method?: string;
    body?: string;
    headers?: Record<string, string>;
  };
  width?: number;
  height?: number;
  onLoad?: () => void;
  onMessage?: (params: { nativeEvent: { data?: string } }) => void;
  onTitleChange?: (title: string) => void;
  scrollEnabled?: boolean;
  injectedJavaScript?: string;
  style?: ViewStyle;
  cookies?: Array<{
    name: string;
    value?: string | null;
    domain?: string;
    path?: string;
  }>;
}

export const WebView: React.FC<WebViewProps> = ({
  title,
  source,
  onLoad,
  onMessage,
  onTitleChange,
  scrollEnabled = true,
  injectedJavaScript,
  style,
  width,
  height,
  cookies,
}) => {
  const [html, setHtml] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const titleObserverRef = useRef<MutationObserver | null>(null);

  // Cookie 설정을 위한 JavaScript 생성
  const getCookieInjectionScript = useCallback(() => {
    if (!cookies || cookies.length === 0) return '';

    const cookieStatements = cookies
      .map(cookie => {
        const { name, value, domain, path = '/' } = cookie;
        let cookieString = `${name}=${value}; path=${path}`;
        if (domain) {
          cookieString += `; domain=${domain}`;
        }
        return `document.cookie = "${cookieString}";`;
      })
      .join('\n');

    return `
      (function() {
        ${cookieStatements}
      })();
    `;
  }, [cookies]);

  const handleInjectedJavaScript = useCallback(
    (html: string | undefined) => {
      const cookieScript = getCookieInjectionScript();
      const combinedScript = [cookieScript, injectedJavaScript]
        .filter(Boolean)
        .join('\n');
      if (!html) return html;

      if (combinedScript) {
        return html.replace(
          '</body>',
          `<script>${combinedScript}</script></body>`,
        );
      }
      return html;
    },
    [injectedJavaScript, getCookieInjectionScript],
  );

  const needsFetch =
    isNotEmpty(source.method) ||
    isNotEmpty(source.body) ||
    (cookies && cookies.length > 0) ||
    isNotEmpty(injectedJavaScript);

  const handleSourceInIFrame = useCallback(() => {
    if (!source.uri || !needsFetch) return;

    // Cookie를 헤더에 추가
    const fetchHeaders: Record<string, string> = {};
    if (cookies && cookies.length > 0) {
      const cookieHeader = cookies.map(c => `${c.name}=${c.value}`).join('; ');
      fetchHeaders['Cookie'] = cookieHeader;
    }

    fetch(source.uri, {
      method: source.method ?? 'GET',
      body: source.body,
      headers: { ...fetchHeaders, ...source.headers },
      credentials: 'include',
    })
      .then(response => response.text())
      .then(html => setHtml(`<base href="${source.uri}" />${html}`));
  }, [
    cookies,
    source.uri,
    source.method,
    source.body,
    source.headers,
    needsFetch,
  ]);

  const handleMessage = useCallback(
    (params: { nativeEvent: { data?: string } }) => {
      if (onMessage) {
        console.log('handleMaessage', params);
        onMessage(params);
      }
    },
    [onMessage],
  );

  // [same-origin] iframe 의 document.title 변경 감시.
  // title 변경엔 전용 DOM 이벤트가 없으므로 <head> 서브트리를 MutationObserver 로 관찰한다.
  // (SPA 가 <title> 노드를 통째로 교체하는 경우까지 잡기 위해 title 요소가 아닌 head 를 관찰)
  // cross-origin 이면 contentDocument 가 null 이라 자동으로 no-op → postMessage 경로로 위임된다.
  const disconnectTitleObserver = useCallback(() => {
    titleObserverRef.current?.disconnect();
    titleObserverRef.current = null;
  }, []);

  const observeTitle = useCallback(() => {
    if (!onTitleChange) return;
    // cross-origin: contentDocument 접근이 SOP 로 차단되어 null 반환 → 관찰 불가.
    const doc = frameRef.current?.contentDocument;
    const head = doc?.head;
    if (!doc || !head) return;
    const emit = () => onTitleChange(doc.title);
    emit(); // 초기 title 반영
    const observer = new MutationObserver(emit);
    observer.observe(head, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    titleObserverRef.current = observer;
  }, [onTitleChange]);

  const handleLoad = useCallback(() => {
    // 전체 페이지 네비게이션마다 문서가 교체되므로 관찰자를 재부착한다.
    disconnectTitleObserver();
    observeTitle();
    onLoad?.();
  }, [disconnectTitleObserver, observeTitle, onLoad]);

  useEffect(() => disconnectTitleObserver, [disconnectTitleObserver]);

  // [cross-origin] contentDocument 접근이 막힌 경우, iframe 내부 페이지가 title 을
  // postMessage 로 보내주면 부모가 받아 반영한다. (프로토콜: { type: 'TITLE_CHANGED', title })
  // same-origin 이면 위 MutationObserver 가 이미 처리하므로 이 리스너는 보조 경로로만 동작한다.
  useEffect(() => {
    if (!onTitleChange) return;
    const handleTitleMessage = (event: MessageEvent) => {
      // 이 iframe 이 보낸 메시지만 신뢰(다른 위젯/광고 프레임의 메시지 차단).
      if (event.source !== frameRef.current?.contentWindow) return;
      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'TITLE_CHANGED' && typeof data.title === 'string') {
          onTitleChange(data.title);
        }
      } catch {
        // title 메시지가 아니면 무시
      }
    };
    window.addEventListener('message', handleTitleMessage);
    return () => window.removeEventListener('message', handleTitleMessage);
  }, [onTitleChange]);

  useEffect(() => {
    handleSourceInIFrame();
  }, [handleSourceInIFrame]);

  // message 이벤트 리스너
  useEffect(() => {
    if (onMessage) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window.addEventListener('message', handleMessage, true);
      return () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        window.removeEventListener('message', handleMessage, true);
      };
    }
  }, [onMessage, handleMessage]);

  return createElement('iframe', {
    title,
    ref: frameRef,
    src: !needsFetch ? source.uri : undefined,
    srcDoc: needsFetch
      ? handleInjectedJavaScript(html ?? source.html ?? undefined)
      : undefined,
    width: width,
    height: height,
    style: StyleSheet.flatten([
      styles.iframe,
      scrollEnabled && styles.noScroll,
      style,
    ]),
    allowFullScreen: true,
    allowpaymentrequest: 'true',
    frameBorder: '0',
    seamless: true,
    onLoad: handleLoad,
  });
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iframe: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
  },
  noScroll: {
    overflow: 'hidden',
  },
});
